import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Tooltip,
  InputAdornment,
  Grid,
  TablePagination,
  Alert,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  QrCode as QrCodeIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Print as PrintIcon,
  QrCode2 as QrCode2Icon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import Checkbox from '@mui/material/Checkbox';
import { sparePartService, categoryService, machineService, supplierService, qrcodeService, storeService } from '../services';
import { useAuthStore } from '../store/authStore';

export interface SparePart {
  id: number;
  part_number: string;
  manufacturer_part_number?: string;
  sap_number?: string;
  part_name: string;
  description?: string;
  part_use?: string;
  current_stock: number;
  minimum_stock: number;
  maximum_stock?: number;
  reorder_point?: number;
  unit_price: number;
  unit_of_measure?: string;
  category_id?: number;
  machine_ids?: number[];
  machines?: any[];
  machine_names?: string;
  supplier_id?: number;
  category_name?: string;
  supplier_name?: string;
  location?: string;
  shelf_position?: string;
  status?: string;
  pds_stock?: number;
  ems_stock?: number;
}

const SpareParts: React.FC = () => {
  const user = useAuthStore((state) => state.user);
  const canEdit = user?.role === 'admin' || user?.role === 'manager';
  
  const [parts, setParts] = useState<SparePart[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [machines, setMachines] = useState<any[]>([]);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [machineFilter, setMachineFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [stockFilter, setStockFilter] = useState<string>('');
  const [locationFilter, setLocationFilter] = useState<string>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [selectedParts, setSelectedParts] = useState<number[]>([]);
  const [qrPdfLoading, setQrPdfLoading] = useState(false);
  const [selectedPart, setSelectedPart] = useState<SparePart | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [viewPart, setViewPart] = useState<SparePart | null>(null);
  const [qrCodeImage, setQrCodeImage] = useState('');
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [formData, setFormData] = useState<Partial<SparePart>>({
    part_number: '',
    manufacturer_part_number: '',
    sap_number: '',
    part_name: '',
    description: '',
    part_use: '',
    unit_of_measure: 'pieces',
    unit_price: 0,
    current_stock: 0,
    minimum_stock: 0,
    maximum_stock: 0,
    reorder_point: 0,
    location: '',
    shelf_position: '',
    status: 'active',
  });

  const getStockColor = (part: SparePart): "error" | "warning" | "success" => {
    if (part.current_stock === 0) return 'error'; // Out of stock - Red
    if (part.current_stock <= part.minimum_stock) return 'warning'; // Low stock - Orange
    if (part.reorder_point && part.current_stock <= part.reorder_point) return 'warning'; // Below reorder point
    return 'success'; // Normal - Green
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [partsData, categoriesData, machinesData, suppliersData] = await Promise.all([
        sparePartService.getAll(),
        categoryService.getAll(),
        machineService.getAll(),
        supplierService.getAll(),
      ]);
      setParts(partsData.parts);
      setCategories(categoriesData.categories);
      setMachines(machinesData.machines);
      setSuppliers(suppliersData.suppliers);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = async (part?: SparePart) => {
    setSaveError(null);
    setSaveSuccess(false);
    if (part) {
      // Fetch full part details including machine_ids
      try {
        const response = await sparePartService.getById(part.id);
        const fullPart = response.part;
        console.log('Full part details:', fullPart);
        console.log('Machine IDs:', fullPart.machine_ids);
        console.log('Machines:', fullPart.machines);
        
        // Get store stock for the part
        let pds_stock = 0;
        let ems_stock = 0;
        try {
          const stockResponse = await storeService.getPartStock(part.id);
          const stockData = stockResponse.stock || [];
          const pdsStockData = stockData.find((s: any) => s.store_id === 1);
          const emsStockData = stockData.find((s: any) => s.store_id === 2);
          pds_stock = pdsStockData?.quantity || 0;
          ems_stock = emsStockData?.quantity || 0;
        } catch (stockError) {
          console.error('Failed to fetch store stock:', stockError);
        }
        
        setSelectedPart(fullPart);
        setFormData({
          part_number: fullPart.part_number || '',
          manufacturer_part_number: fullPart.manufacturer_part_number || '',
          sap_number: fullPart.sap_number || '',
          part_name: fullPart.part_name || '',
          description: fullPart.description || '',
          part_use: fullPart.part_use || '',
          unit_of_measure: fullPart.unit_of_measure || 'pieces',
          unit_price: fullPart.unit_price || 0,
          current_stock: fullPart.current_stock || 0,
          minimum_stock: fullPart.minimum_stock || 0,
          maximum_stock: fullPart.maximum_stock || 0,
          reorder_point: fullPart.reorder_point || 0,
          location: fullPart.location || '',
          shelf_position: fullPart.shelf_position || '',
          status: fullPart.status || 'active',
          category_id: fullPart.category_id,
          supplier_id: fullPart.supplier_id,
          machine_ids: fullPart.machine_ids || [],
          pds_stock,
          ems_stock,
        });
        console.log('Form data set:', {
          machine_ids: fullPart.machine_ids || [],
          pds_stock,
          ems_stock,
        });
      } catch (error) {
        console.error('Failed to fetch part details:', error);
        // Fallback to the part data we have
        setSelectedPart(part);
        setFormData({
          ...part,
          machine_ids: part.machine_ids || [],
          pds_stock: part.pds_stock || 0,
          ems_stock: part.ems_stock || 0,
        });
      }
    } else {
      setSelectedPart(null);
      setFormData({
        part_number: '',
        manufacturer_part_number: '',
        sap_number: '',
        part_name: '',
        description: '',
        part_use: '',
        unit_of_measure: 'pieces',
        unit_price: 0,
        current_stock: 0,
        minimum_stock: 0,
        maximum_stock: 0,
        reorder_point: 0,
        location: '',
        shelf_position: '',
        status: 'active',
        machine_ids: [],
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedPart(null);
  };

  const handleSave = async () => {
    // Validate required fields
    if (!formData.part_name?.trim()) {
      setSaveError('Part Name is required');
      return;
    }

    setSaveLoading(true);
    setSaveError(null);
    setSaveSuccess(false);
    
    try {
      let partId = selectedPart?.id;
      
      if (selectedPart) {
        await sparePartService.update(selectedPart.id, formData);
      } else {
        const result = await sparePartService.create(formData);
        partId = result.part.id;
      }

      // Update store stock if provided
      if (partId) {
        // Update PDS stock (store_id = 1)
        await storeService.updatePartStock(partId, 1, {
          quantity: formData.pds_stock || 0,
          location: formData.location,
          shelf_position: formData.shelf_position
        });

        // Update EMS stock (store_id = 2)
        await storeService.updatePartStock(partId, 2, {
          quantity: formData.ems_stock || 0
        });
      }

      setSaveSuccess(true);
      setTimeout(() => {
        handleCloseDialog();
        loadData();
      }, 1000);
    } catch (error: any) {
      console.error('Failed to save part:', error);
      setSaveError(error.response?.data?.error || 'Failed to save part. Please try again.');
    } finally {
      setSaveLoading(false);
    }
  };

  const handlePrint = async () => {
    setPdfLoading(true);
    try {
      // Build query parameters
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (categoryFilter) params.append('categoryId', categoryFilter);
      if (machineFilter) params.append('machine', machineFilter);
      if (statusFilter) params.append('status', statusFilter);
      if (stockFilter) params.append('stockLevel', stockFilter);

      // Get auth token
      const token = localStorage.getItem('token');
      
      if (!token) {
        alert('Please login to generate PDF reports.');
        setPdfLoading(false);
        return;
      }

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';
      const pdfUrl = `${apiUrl}/reports/spare-parts-pdf?${params.toString()}`;
      
      console.log('Generating PDF with filters:', { searchTerm, categoryFilter, machineFilter, statusFilter, stockFilter });
      console.log('PDF URL:', pdfUrl);
      
      // Fetch PDF from backend
      const response = await fetch(pdfUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('PDF generation failed:', errorText);
        throw new Error(`Failed to generate PDF: ${response.statusText}`);
      }

      // Create blob from response
      const blob = await response.blob();
      console.log('PDF blob size:', blob.size, 'type:', blob.type);
      
      if (blob.size === 0) {
        throw new Error('Generated PDF is empty');
      }
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `spare-parts-report-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      console.log('PDF download initiated successfully');
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert(`Failed to generate PDF report: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setPdfLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this part?')) {
      try {
        await sparePartService.delete(id);
        loadData();
      } catch (error) {
        console.error('Failed to delete part:', error);
      }
    }
  };

  const handleGenerateQR = async (part: SparePart) => {
    try {
      const response = await qrcodeService.generate(part.id);
      setQrCodeImage(response.qr_code);
      setSelectedPart(part);
      setQrDialogOpen(true);
    } catch (error) {
      console.error('Failed to generate QR code:', error);
    }
  };

  const handleViewDetails = async (part: SparePart) => {
    try {
      const response = await sparePartService.getById(part.id);
      setViewPart(response.part);
      setDetailsDialogOpen(true);
    } catch (error) {
      console.error('Failed to load part details:', error);
    }
  };

  const handleCloseDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setViewPart(null);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allPartIds = filteredParts.map((part) => part.id);
      setSelectedParts(allPartIds);
    } else {
      setSelectedParts([]);
    }
  };

  const handleSelectPart = (partId: number) => {
    setSelectedParts((prev) =>
      prev.includes(partId)
        ? prev.filter((id) => id !== partId)
        : [...prev, partId]
    );
  };

  const handlePrintQRCodes = async () => {
    if (selectedParts.length === 0) {
      alert('Please select at least one spare part to generate QR codes.');
      return;
    }

    setQrPdfLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Please login to generate QR codes.');
        setQrPdfLoading(false);
        return;
      }

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';
      
      // Get CSRF token
      const csrfResponse = await fetch(`${apiUrl}/csrf-token`, {
        credentials: 'include',
      });
      const { csrfToken } = await csrfResponse.json();

      const response = await fetch(`${apiUrl}/reports/qr-codes-pdf`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        credentials: 'include',
        body: JSON.stringify({ partIds: selectedParts }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate QR codes PDF');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `qr-codes-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

      setSelectedParts([]);
    } catch (error) {
      console.error('Error generating QR codes PDF:', error);
      alert(`Failed to generate QR codes: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setQrPdfLoading(false);
    }
  };

  const filteredParts = parts.filter((part) => {
    // Search filter
    const matchesSearch = searchTerm === '' || 
      part.part_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      part.part_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (part.sap_number && part.sap_number.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (part.description && part.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    // Category filter
    const matchesCategory = categoryFilter === '' || part.category_id?.toString() === categoryFilter;
    
    // Machine filter
    const matchesMachine = machineFilter === '' || 
      (part.machine_names && part.machine_names.toLowerCase().includes(machineFilter.toLowerCase()));
    
    // Status filter
    const matchesStatus = statusFilter === '' || part.status === statusFilter;
    
    // Stock filter
    let matchesStock = true;
    if (stockFilter === 'out') {
      matchesStock = part.current_stock === 0;
    } else if (stockFilter === 'low') {
      matchesStock = part.current_stock > 0 && part.current_stock <= part.minimum_stock;
    } else if (stockFilter === 'normal') {
      matchesStock = part.current_stock > part.minimum_stock;
    }
    
    // Location filter
    const matchesLocation = locationFilter === '' || 
      (part.location && part.location.toLowerCase().includes(locationFilter.toLowerCase()));
    
    return matchesSearch && matchesCategory && matchesMachine && matchesStatus && matchesStock && matchesLocation;
  });

  const getStockStatus = (part: SparePart) => {
    if (part.current_stock === 0) return 'error';
    if (part.reorder_point && part.current_stock <= part.reorder_point) return 'warning';
    return 'success';
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Spare Parts</Typography>
        <Box display="flex" gap={2}>
          <Button startIcon={<RefreshIcon />} onClick={loadData}>
            Refresh
          </Button>
          {canEdit && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
              Add Part
            </Button>
          )}
        </Box>
      </Box>

      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        <TextField
          size="small"
          placeholder="Search parts..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ minWidth: 250 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size="small"
          label="Category"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">All Categories</MenuItem>
          {categories.map((cat) => (
            <MenuItem key={cat.id} value={cat.id.toString()}>
              {cat.category_name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Machine"
          value={machineFilter}
          onChange={(e) => setMachineFilter(e.target.value)}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">All Machines</MenuItem>
          {machines.map((machine) => (
            <MenuItem key={machine.id} value={machine.machine_name}>
              {machine.machine_name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          sx={{ minWidth: 120 }}
        >
          <MenuItem value="">All Status</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="discontinued">Discontinued</MenuItem>
          <MenuItem value="obsolete">Obsolete</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label="Stock Level"
          value={stockFilter}
          onChange={(e) => setStockFilter(e.target.value)}
          sx={{ minWidth: 120 }}
        >
          <MenuItem value="">All Levels</MenuItem>
          <MenuItem value="out">Out of Stock</MenuItem>
          <MenuItem value="low">Low Stock</MenuItem>
          <MenuItem value="normal">Normal</MenuItem>
        </TextField>
        <TextField
          size="small"
          label="Location"
          value={locationFilter}
          onChange={(e) => setLocationFilter(e.target.value)}
          placeholder="Filter by location"
          sx={{ minWidth: 150 }}
        />
        {(searchTerm || categoryFilter || machineFilter || statusFilter || stockFilter || locationFilter) && (
          <Button 
            size="small" 
            onClick={() => {
              setSearchTerm('');
              setCategoryFilter('');
              setMachineFilter('');
              setStatusFilter('');
              setStockFilter('');
              setLocationFilter('');
            }}
          >
            Clear Filters
          </Button>
        )}
        {canEdit && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<QrCode2Icon />}
            onClick={handlePrintQRCodes}
            disabled={qrPdfLoading || selectedParts.length === 0}
            sx={{ ml: 'auto' }}
          >
            {qrPdfLoading ? 'Generating...' : `QR Codes (${selectedParts.length})`}
          </Button>
        )}
        <Button
          variant="outlined"
          size="small"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          disabled={pdfLoading}
        >
          {pdfLoading ? 'Generating PDF...' : `Print (${filteredParts.length})`}
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {canEdit && (
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedParts.length > 0 && selectedParts.length < filteredParts.length}
                    checked={filteredParts.length > 0 && selectedParts.length === filteredParts.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
              )}
              <TableCell>Part Number</TableCell>
              <TableCell>Part Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Machines</TableCell>
              <TableCell align="right">Total Stock</TableCell>
              <TableCell align="right">PDS Stock</TableCell>
              <TableCell align="right">EMS Stock</TableCell>
              <TableCell align="right">Min</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredParts
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((part) => (
              <TableRow key={part.id}>
                {canEdit && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedParts.includes(part.id)}
                      onChange={() => handleSelectPart(part.id)}
                    />
                  </TableCell>
                )}
                <TableCell>{part.part_number}</TableCell>
                <TableCell>{part.part_name}</TableCell>
                <TableCell>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                    {part.description || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                    {part.location || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                    {part.machine_names || '-'}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Chip label={part.current_stock} size="small" color={getStockColor(part)} />
                </TableCell>
                <TableCell align="right">
                  <Chip label={part.pds_stock || 0} size="small" variant="outlined" />
                </TableCell>
                <TableCell align="right">
                  <Chip label={part.ems_stock || 0} size="small" variant="outlined" />
                </TableCell>
                <TableCell align="right">{part.minimum_stock}</TableCell>
                <TableCell align="center">
                  <Tooltip title="View Details">
                    <IconButton size="small" onClick={() => handleViewDetails(part)}>
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Generate QR Code">
                    <IconButton size="small" onClick={() => handleGenerateQR(part)}>
                      <QrCodeIcon />
                    </IconButton>
                  </Tooltip>
                  {canEdit && (
                    <>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleOpenDialog(part)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => handleDelete(part.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {filteredParts.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography color="textSecondary">No parts found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredParts.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{selectedPart ? 'Edit Part' : 'Add New Part'}</DialogTitle>
        <DialogContent>
          {saveError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {saveError}
            </Alert>
          )}
          {saveSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Part saved successfully!
            </Alert>
          )}
          
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Basic Information Section */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold', mb: 1 }}>
                Basic Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Part Name *"
                value={formData.part_name}
                onChange={(e) => setFormData({ ...formData, part_name: e.target.value })}
                required
                error={saveError !== null && !formData.part_name}
                helperText={saveError !== null && !formData.part_name ? "Part name is required" : ""}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Use"
                value={formData.part_use}
                onChange={(e) => setFormData({ ...formData, part_use: e.target.value })}
                placeholder="How this part is used"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={2}
                placeholder="Detailed description of the part"
              />
            </Grid>
            
            {/* Part Numbers Section */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold', mb: 1, mt: 2 }}>
                Part Numbers
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="System Part Number"
                value={formData.part_number || 'Auto-generated'}
                disabled
                helperText="Auto-generated by system"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Manufacturer Part Number"
                value={formData.manufacturer_part_number}
                onChange={(e) => setFormData({ ...formData, manufacturer_part_number: e.target.value })}
                placeholder="Supplier's part number"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="SAP Number"
                value={formData.sap_number}
                onChange={(e) => setFormData({ ...formData, sap_number: e.target.value })}
                placeholder="SAP system number"
              />
            </Grid>
            
            {/* Category & Relations Section */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold', mb: 1, mt: 2 }}>
                Category & Relations
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Category"
                value={formData.category_id || ''}
                onChange={(e) => setFormData({ ...formData, category_id: Number(e.target.value) })}
              >
                <MenuItem value="">None</MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.category_name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Supplier"
                value={formData.supplier_id || ''}
                onChange={(e) => setFormData({ ...formData, supplier_id: Number(e.target.value) })}
              >
                <MenuItem value="">None</MenuItem>
                {suppliers.map((supplier) => (
                  <MenuItem key={supplier.id} value={supplier.id}>
                    {supplier.supplier_name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Machines"
                value={formData.machine_ids || []}
                onChange={(e) => setFormData({ ...formData, machine_ids: e.target.value as unknown as number[] })}
                SelectProps={{
                  multiple: true,
                  renderValue: (selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as number[]).map((value) => {
                        const machine = machines.find(m => m.id === value);
                        return <Chip key={value} label={machine?.machine_name || 'Unknown'} size="small" />;
                      })}
                    </Box>
                  ),
                }}
                helperText="Select all machines that use this part"
              >
                {machines.map((machine) => (
                  <MenuItem key={machine.id} value={machine.id}>
                    {machine.machine_name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            
            {/* Stock Levels Section */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold', mb: 1, mt: 2 }}>
                Stock Levels
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="PDS Stock (Printing Dept.)"
                type="number"
                value={formData.pds_stock || 0}
                onChange={(e) => setFormData({ ...formData, pds_stock: Number(e.target.value) })}
                helperText="Stock in Printing Department Stores"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="EMS Stock (Eng. & Maint.)"
                type="number"
                value={formData.ems_stock || 0}
                onChange={(e) => setFormData({ ...formData, ems_stock: Number(e.target.value) })}
                helperText="Stock in Engineering & Maintenance Stores"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Total Stock"
                type="number"
                value={(formData.pds_stock || 0) + (formData.ems_stock || 0)}
                disabled
                helperText="Auto-calculated from stores"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Minimum Stock"
                type="number"
                value={formData.minimum_stock}
                onChange={(e) => setFormData({ ...formData, minimum_stock: Number(e.target.value) })}
                helperText="Low stock alert threshold"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Maximum Stock"
                type="number"
                value={formData.maximum_stock}
                onChange={(e) => setFormData({ ...formData, maximum_stock: Number(e.target.value) })}
                helperText="Maximum stock capacity"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Reorder Point"
                type="number"
                value={formData.reorder_point}
                onChange={(e) => setFormData({ ...formData, reorder_point: Number(e.target.value) })}
                helperText="Trigger for reordering"
              />
            </Grid>
            
            {/* Pricing & Location Section */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold', mb: 1, mt: 2 }}>
                Pricing & Location
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Unit of Measure"
                value={formData.unit_of_measure}
                onChange={(e) => setFormData({ ...formData, unit_of_measure: e.target.value })}
                placeholder="e.g., pieces, kg, liters"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Unit Price"
                type="number"
                value={formData.unit_price}
                onChange={(e) => setFormData({ ...formData, unit_price: Number(e.target.value) })}
                InputProps={{
                  startAdornment: <InputAdornment position="start">Rs.</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Location"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="Storage location"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Shelf Position"
                value={formData.shelf_position}
                onChange={(e) => setFormData({ ...formData, shelf_position: e.target.value })}
                placeholder="Shelf/bin number"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saveLoading}>Cancel</Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            disabled={saveLoading}
            startIcon={saveLoading ? <CircularProgress size={20} /> : null}
          >
            {saveLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={qrDialogOpen} onClose={() => setQrDialogOpen(false)}>
        <DialogTitle>QR Code - {selectedPart?.part_name}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" alignItems="center" p={2}>
            {qrCodeImage && <img src={qrCodeImage} alt="QR Code" style={{ width: 300, height: 300 }} />}
            <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
              Part Number: {selectedPart?.part_number}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog open={detailsDialogOpen} onClose={handleCloseDetailsDialog} maxWidth="md" fullWidth>
        <DialogTitle>Spare Part Details</DialogTitle>
        <DialogContent>
          {viewPart && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">Part Number</Typography>
                <Typography variant="body1">{viewPart.part_number || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">Part Name</Typography>
                <Typography variant="body1">{viewPart.part_name || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">Manufacturer Part Number</Typography>
                <Typography variant="body1">{viewPart.manufacturer_part_number || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">SAP Number</Typography>
                <Typography variant="body1">{viewPart.sap_number || '-'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">Description</Typography>
                <Typography variant="body1">{viewPart.description || '-'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">Part Use</Typography>
                <Typography variant="body1">{viewPart.part_use || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">Category</Typography>
                <Typography variant="body1">{viewPart.category_name || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">Machines</Typography>
                <Typography variant="body1">{viewPart.machine_names || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">Supplier</Typography>
                <Typography variant="body1">{viewPart.supplier_name || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">Status</Typography>
                <Chip 
                  label={viewPart.status === 'active' ? 'Active' : 'Inactive'} 
                  color={viewPart.status === 'active' ? 'success' : 'default'} 
                  size="small" 
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="textSecondary">Current Stock</Typography>
                <Chip 
                  label={viewPart.current_stock} 
                  color={getStockColor(viewPart)} 
                  size="small" 
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="textSecondary">PDS Stock</Typography>
                <Typography variant="body1">{viewPart.pds_stock || 0}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="textSecondary">EMS Stock</Typography>
                <Typography variant="body1">{viewPart.ems_stock || 0}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="textSecondary">Minimum Stock</Typography>
                <Typography variant="body1">{viewPart.minimum_stock || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="textSecondary">Maximum Stock</Typography>
                <Typography variant="body1">{viewPart.maximum_stock || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="textSecondary">Reorder Point</Typography>
                <Typography variant="body1">{viewPart.reorder_point || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">Unit Price</Typography>
                <Typography variant="body1">Rs. {viewPart.unit_price ? Number(viewPart.unit_price).toFixed(2) : '0.00'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">Unit of Measure</Typography>
                <Typography variant="body1">{viewPart.unit_of_measure || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">Location</Typography>
                <Typography variant="body1">{viewPart.location || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">Shelf Position</Typography>
                <Typography variant="body1">{viewPart.shelf_position || '-'}</Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetailsDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SpareParts;
